import React, { useContext, useEffect, useRef, useState } from "react";
import { Menu, MenuItem, IconButton, TextField, Typography, Box } from "@mui/material";
import EcomAndCatalogProduct from "./EcomAndCatalogProduct";
import CategoryList from "./CategoryList";
import BrandList from "./BrandList";
import RegionList from "./RegionList";
import TraitList from "./TraitList";
import SubTraitList from "./SubTraitList";
import StrainList from "./StrainList";
import WeekdayList from "./WeekdayList";
import SpecificPeriodPicker from "./SpecificPeriodPicker";
import { promoRuleOptions, maxNumberRegex } from "./data";
import { ConstructionOutlined } from "@mui/icons-material";
import { PlatformContext } from "./AddPromotions";
import { getPlatformProducts } from "../../redux/slices/promotions";

function DropdownMenuDemo({
  menuTrigger = <></>,
  isOpen = false,
  menuOptions = [],
  setOpen = () => { },
  ruleType = "",
  rule = "",
  selectRuleHandler = () => { },
  type = "",
  inputType = "",
  listType = "",
  inputValue = "",
  showError = false,
  ruleId = "",
  attributes = "",
  traitId = null,
  maxInputValue = 0,
  resetCreteria,
  searchTerm = "",
  onSearchChange = () => { },
  disableListSearch = false,
  plainTextTrigger = false,
}) {
  const platform = useContext(PlatformContext);
  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
      // Robust check for datepicker elements
      const target = event.target;
      const isDatepicker =
        target.closest('.react-datepicker-popper') ||
        target.closest('.react-datepicker') ||
        target.className?.toString().includes('react-datepicker');

      if (isDatepicker) return;

      // If target is no longer in body, it might be the datepicker that just unmounted
      if (!document.body.contains(target)) return;
    }
    setAnchorEl(null);
    setOpen(false);
  };

  const iconClass = () => {
    if ((ruleId === "if_rule" || ruleId === "then_rule") && type === "select") {
      return promoRuleOptions?.find((option) => option.value === inputValue)?.iconColor;
    }
    if (
      (typeof inputValue === "string" && inputValue !== "") ||
      (typeof inputValue === "object" &&
        inputValue !== null &&
        Object.keys(inputValue).length > 0) ||
      (Array.isArray(inputValue) && inputValue.length > 0)
    ) {
      return "primary.main"; // fallback MUI theme color
    }

    return "inherit";
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      if (type === "input" && inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [type, isOpen]);

  return (
    <Box>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
          color: showError ? "#FD4438" : (plainTextTrigger ? "inherit" : iconClass()),
          marginLeft: "4px",
          marginRight: "4px",
          textDecoration: plainTextTrigger ? "none" : "underline",
          textUnderlineOffset: "4px",
          "&:hover": {
            color: showError ? "#FD4438" : (plainTextTrigger ? "inherit" : "#475cc7"),
          },
        }}
        className={`check-action ${!plainTextTrigger && (showError ? "" : "text-blue-400")} ${!plainTextTrigger ? iconClass() : ""}`}
      >
        {menuTrigger}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && isOpen}
        onClose={handleClose}
        disableAutoFocusItem
        transitionDuration={300}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            maxWidth: type === "input" ? 200 : type === "date_range" ? 400 : undefined,
            boxShadow: type === "input" ? "none" : undefined,
            maxHeight: 500,
            overflowY: type === "date_range" ? "visible" : "auto",
            overflowX: type === "date_range" ? "visible" : "auto",
          },
        }}
        MenuListProps={{
          sx: { overflow: type === "date_range" ? "visible" : "auto" }
        }}
        onEntered={() => {
          if (type === "input" && inputRef.current) {
            inputRef.current.focus();
          }
        }}
        className="promotion-popover"
      >
        {type === "date_range" && (
          <SpecificPeriodPicker
            inputValue={inputValue}
            selectRuleHandler={selectRuleHandler}
            rule={rule}
            ruleType={ruleType}
            setOpen={setOpen}
          />
        )}
        {type === "input" && (
          <MenuItem disableRipple disableGutters>
            <TextField
              fullWidth
              placeholder="Enter value"
              inputRef={inputRef}
              value={inputValue || ""}
              type={inputType === "date" ? "date" : inputType === "time" ? "time" : "text"}
              variant="standard"
              error={showError}
              onChange={(e) => {
                const val = e.target.value;

                if (!val) {
                  selectRuleHandler({ ruleValue: val });
                  return;
                }

                if (inputType === "date" || inputType === "time") {
                  selectRuleHandler({ ruleValue: val });
                  return;
                }

                // Handle userSearch (text input) validation
                if (inputType === "userSearch") {
                  if (val.length <= 50) {
                    const cleanValue = val.replace(/[^a-zA-Z0-9\s]/g, "");
                    // Truncate to 50 characters if needed
                    const finalValue = cleanValue.slice(0, 50);
                    selectRuleHandler({ ruleValue: finalValue });
                    return;
                  }
                  return;
                }

                // Handle numeric input validation
                if (inputType === "number") {
                  if (/^\d+$/.test(val)) {
                    selectRuleHandler({ ruleValue: val });
                  }
                  return;
                }

                if (inputType === "dollar" || inputType === "percentage" || inputType === "age") {
                  const decimalRegex = /^\d+(\.\d{0,2})?$/;

                  if (!decimalRegex.test(val)) {
                    return; // Prevent input if it doesn't match the pattern
                  }
                  const numericValue = +val;
                  const isValidNumber = !isNaN(numericValue);
                  const isPercentageValid = inputType !== "percentage" || numericValue <= 100;
                  const isAgeValid = inputType !== "age" || numericValue <= 100;
                  const isMaxValueValid = !(
                    maxInputValue > 0 &&
                    ruleId === "flat_discount" &&
                    numericValue > maxInputValue
                  );

                  if (
                    isValidNumber &&
                    isPercentageValid &&
                    isAgeValid &&
                    maxNumberRegex.test(numericValue) &&
                    isMaxValueValid
                  ) {
                    selectRuleHandler({ ruleValue: val });
                  }
                  return;
                }

                // Default numeric validation (for other number inputs)
                if (/^[1-9]\d{0,6}$/.test(val)) {
                  selectRuleHandler({ ruleValue: val });
                }
              }}
              inputProps={{
                sx: { padding: "6px 8px" },
              }}
            />
          </MenuItem>
        )}

        {type === "list" && listType === "product" && (
          <EcomAndCatalogProduct
            changeHandler={selectRuleHandler}
            inputValue={inputValue}
            listType={listType}
            setAnchorEl={setAnchorEl}
          />
        )}

        {type === "list" && listType === "free_product" && (
          <EcomAndCatalogProduct
            changeHandler={selectRuleHandler}
            // filterType={["catalog", "ecommerce"]}
            inputValue={inputValue}
            listType={listType}
            setAnchorEl={setAnchorEl}
          />
        )}
        {type === "list" && listType === "free_bogo_product" && (
          <EcomAndCatalogProduct
            changeHandler={selectRuleHandler}
            // filterType={["catalog", "ecommerce"]}
            inputValue={inputValue}
            listType={listType}
            setAnchorEl={setAnchorEl}
          />
        )}

        {type === "list" && listType === "category" && (
          <CategoryList
            changeHandler={selectRuleHandler}
            inputValue={inputValue}
            listType={listType}
            setAnchorEl={setAnchorEl}
          />
        )}

        {type === "list" && listType === "brand" && (
          <BrandList
            changeHandler={selectRuleHandler}
            inputValue={inputValue}
            externalSearchTerm={searchTerm}
            disableInternalSearch={disableListSearch}
          />
        )}

        {type === "list" && listType === "region" && (
          <RegionList
            changeHandler={selectRuleHandler}
            inputValue={inputValue}
            setAnchorEl={setAnchorEl}
          />
        )}
        {type === "list" && listType === "weekdays" && (
          <WeekdayList
            changeHandler={selectRuleHandler}
            inputValue={inputValue}
            setOpen={setOpen}
          />
        )}

        {type === "one-level" && attributes === "product_trait" && listType !== "sub_trait" && (
          <TraitList
            changeHandler={(payload) => {
              selectRuleHandler(payload);
              setOpen(false);
            }}
            inputValue={inputValue}
            setAnchorEl={setAnchorEl}
            platformType={platform ? String(platform).toLowerCase() : "hemp"}
          />
        )}

        {type === "one-level" && attributes === "product_strain" && (
          <StrainList
            changeHandler={(payload) => {
              selectRuleHandler(payload);
              setOpen(false);
            }}
            inputValue={inputValue}
            setAnchorEl={setAnchorEl}
          />
        )}

        {type === "one-level" && attributes === "product_trait" && listType === "sub_trait" && traitId && (
          <SubTraitList
            traitId={traitId}
            changeHandler={(payload) => selectRuleHandler(payload)}
            inputValue={inputValue}
            setAnchorEl={setAnchorEl}
            platformType={platform ? String(platform).toLowerCase() : "hemp"}
          />
        )}

        {type === "list" &&
          listType !== "product" &&
          listType !== "free_product" &&
          listType !== "free_bogo_product" &&
          listType !== "category" &&
          listType !== "promotion" &&
          listType !== "brand" &&
          listType !== "region" &&
          listType !== "weekdays" &&
          (menuOptions?.length === 0 ? (
            <Box sx={{ padding: 2, transform: "scale(0.9)" }}>Data Not Found</Box>
          ) : (
            menuOptions.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === inputValue}
                onClick={() =>
                  selectRuleHandler({
                    rule,
                    ruleType,
                    ruleValue: option.value,
                  })
                }
              >
                {!option.icon ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    style={{ marginRight: 4 }}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.2978 7.50022C12.2714 10.3996 9.89959 12.7286 7.00022 12.7022C4.10084 12.6758 1.77185 10.3039 1.79826 7.40457L1.84608 2.15479L12.3456 2.25044L12.2978 7.50022ZM7.00891 11.7477C9.38113 11.7693 11.3217 9.86374 11.3433 7.49152L11.3824 3.19625L2.79189 3.11799L2.75277 7.41327C2.73116 9.78548 4.6367 11.7261 7.00891 11.7477Z"
                      fill="#475CC7"
                    />
                  </svg>
                ) : (
                  <span
                    className={`material-symbols-rounded ${option.iconClass}`}
                    style={{ color: option.iconColor }}
                  >
                    {option.icon}
                  </span>
                )}
                <Typography sx={{ paddingLeft: 1 }}>
                  {`${option.title || ""} ${option.highlightText || ""} ${option.endTitle || ""} ${option.isGroupRule && option.groupRule
                    ? `${option.groupRule.title || ""} ${option.groupRule.highlightText || ""} ${option.groupRule.endTitle || ""
                    }`
                    : ""
                    }`}
                </Typography>
              </MenuItem>
            ))
          ))}


        {type === "select" &&
          menuOptions?.length > 0 &&
          menuOptions
            .filter((option) => !option?.exclude?.includes(platform) || !option?.exclude)
            .map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === inputValue}
                onClick={() => {
                  if (typeof resetCreteria === "function") {
                    resetCreteria();
                  }
                  selectRuleHandler({ rule, ruleType, ruleValue: option.value });
                  setOpen(false);
                }}
              >
                {!option.icon ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    style={{ marginRight: 4 }}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.2978 7.50022C12.2714 10.3996 9.89959 12.7286 7.00022 12.7022C4.10084 12.6758 1.77185 10.3039 1.79826 7.40457L1.84608 2.15479L12.3456 2.25044L12.2978 7.50022ZM7.00891 11.7477C9.38113 11.7693 11.3217 9.86374 11.3433 7.49152L11.3824 3.19625L2.79189 3.11799L2.75277 7.41327C2.73116 9.78548 4.6367 11.7261 7.00891 11.7477Z"
                      fill="#475CC7"
                    />
                  </svg>
                ) : (
                  <span
                    className={`material-symbols-rounded ${option.iconClass}`}
                    style={{ color: option.iconColor }}
                  >
                    {option.icon}
                  </span>
                )}
                <Typography sx={{ paddingLeft: 1 }}>
                  {`${option.title || option?.selectedValue || ""} ${option.highlightText || ""} ${option.endTitle || ""
                    } ${option.isGroupRule && option.groupRule
                      ? `${option.groupRule.title || ""} ${option.groupRule.highlightText || ""} ${option.groupRule.endTitle || ""
                      }`
                      : ""
                    }`}
                </Typography>
              </MenuItem>
            ))}
      </Menu>
    </Box>
  );
}

export default DropdownMenuDemo;
